'use client';

import Icon from '@/components/ui/AppIcon';

interface GuestDetailsProps {
  formData: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    company: string;
    guestType: string;
    adults: number;
    children: number;
    specialRequests: string;
  };
  onFormChange: (field: string, value: string | number) => void;
}

export default function GuestDetails({ formData, onFormChange }: GuestDetailsProps) {
  const guestTypes = [
    { value: 'business', label: 'Business Traveler' },
    { value: 'leisure', label: 'Leisure/Vacation' },
    { value: 'relocation', label: 'Relocation/Extended Stay' },
    { value: 'corporate', label: 'Corporate Group' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-display font-semibold text-[var(--color-text-primary)] mb-2">
          Guest Information
        </h2>
        <p className="text-[var(--color-text-secondary)]">
          Please provide your details for the reservation
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-[var(--color-text-primary)]">
            First Name <span className="text-[var(--color-error)]">*</span>
          </label>
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => onFormChange('firstName', e.target.value)}
            placeholder="Enter first name"
            className="w-full px-4 py-3 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent transition-all duration-300"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-[var(--color-text-primary)]">
            Last Name <span className="text-[var(--color-error)]">*</span>
          </label>
          <input
            type="text"
            value={formData.lastName}
            onChange={(e) => onFormChange('lastName', e.target.value)}
            placeholder="Enter last name"
            className="w-full px-4 py-3 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent transition-all duration-300"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-[var(--color-text-primary)]">
            Email Address <span className="text-[var(--color-error)]">*</span>
          </label>
          <div className="relative">
            <input
              type="email"
              value={formData.email}
              onChange={(e) => onFormChange('email', e.target.value)}
              placeholder="your.email@example.com"
              className="w-full px-4 py-3 pl-12 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent transition-all duration-300"
            />
            <Icon
              name="EnvelopeIcon"
              size={20}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--color-text-secondary)]"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-[var(--color-text-primary)]">
            Phone Number <span className="text-[var(--color-error)]">*</span>
          </label>
          <div className="relative">
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => onFormChange('phone', e.target.value)}
              placeholder="+91 98765 43210"
              className="w-full px-4 py-3 pl-12 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent transition-all duration-300"
            />
            <Icon
              name="PhoneIcon"
              size={20}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--color-text-secondary)]"
            />
          </div>
        </div>

        <div className="space-y-2 md:col-span-2">
          <label className="block text-sm font-medium text-[var(--color-text-primary)]">
            Guest Type <span className="text-[var(--color-error)]">*</span>
          </label>
          <select
            value={formData.guestType}
            onChange={(e) => onFormChange('guestType', e.target.value)}
            className="w-full px-4 py-3 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent transition-all duration-300"
          >
            <option value="">Select guest type</option>
            {guestTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {(formData.guestType === 'business' || formData.guestType === 'corporate') && (
          <div className="space-y-2 md:col-span-2">
            <label className="block text-sm font-medium text-[var(--color-text-primary)]">
              Company Name
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.company}
                onChange={(e) => onFormChange('company', e.target.value)}
                placeholder="Enter company name"
                className="w-full px-4 py-3 pl-12 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent transition-all duration-300"
              />
              <Icon
                name="BuildingOfficeIcon"
                size={20}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--color-text-secondary)]"
              />
            </div>
          </div>
        )}

        <div className="space-y-2">
          <label className="block text-sm font-medium text-[var(--color-text-primary)]">
            Number of Adults <span className="text-[var(--color-error)]">*</span>
          </label>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => onFormChange('adults', Math.max(1, formData.adults - 1))}
              className="w-10 h-10 flex items-center justify-center border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-muted)] transition-colors duration-300"
            >
              <Icon name="MinusIcon" size={20} />
            </button>
            <span className="text-lg font-semibold text-[var(--color-text-primary)] w-12 text-center">
              {formData.adults}
            </span>
            <button
              type="button"
              onClick={() => onFormChange('adults', formData.adults + 1)}
              className="w-10 h-10 flex items-center justify-center border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-muted)] transition-colors duration-300"
            >
              <Icon name="PlusIcon" size={20} />
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-[var(--color-text-primary)]">
            Number of Children
          </label>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => onFormChange('children', Math.max(0, formData.children - 1))}
              className="w-10 h-10 flex items-center justify-center border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-muted)] transition-colors duration-300"
            >
              <Icon name="MinusIcon" size={20} />
            </button>
            <span className="text-lg font-semibold text-[var(--color-text-primary)] w-12 text-center">
              {formData.children}
            </span>
            <button
              type="button"
              onClick={() => onFormChange('children', formData.children + 1)}
              className="w-10 h-10 flex items-center justify-center border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-muted)] transition-colors duration-300"
            >
              <Icon name="PlusIcon" size={20} />
            </button>
          </div>
        </div>

        <div className="space-y-2 md:col-span-2">
          <label className="block text-sm font-medium text-[var(--color-text-primary)]">
            Special Requests or Requirements
          </label>
          <textarea
            value={formData.specialRequests}
            onChange={(e) => onFormChange('specialRequests', e.target.value)}
            placeholder="Any special requests, dietary requirements, accessibility needs, or preferences..."
            rows={4}
            className="w-full px-4 py-3 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent transition-all duration-300 resize-none"
          />
        </div>
      </div>
    </div>
  );
}